import { z } from 'zod';

import { useAppForm } from '@/app/lib/tanstack-form';
import { idBasedLookupSchema } from '@/app/network/models/refs';
import { sanitizeStringForApi } from '@/common/sanitization';

export const createUpdateContactFormSchema = z.object({
  client: idBasedLookupSchema.nullable(),
  firstName: z.string()
    .min(1, 'First Name is required')
    .max(35, 'First Name is too long'),
  lastName: z.string()
    .min(1, 'Last Name is required')
    .max(35, 'Last Name is too long'),
  title: z.string()
    .max(50, 'Title is too long')
    .optional(),
  email: z.string()
    .max(255, 'Email is too long')
    .optional(),
  workPhone: z.string()
    .max(20, 'Work Phone is too long')
    .optional(),
  mobilePhone: z.string()
    .max(20, 'Mobile Phone is too long')
    .optional(),
  addressLine1: z.string()
    .max(100, 'Address Line 1 is too long')
    .optional(),
  addressLine2: z.string()
    .max(50, 'Address Line 2 is too long')
    .optional(),
  city: z.string()
    .max(25, 'City is too long')
    .optional(),
  state: z.string()
    .optional(),
  zipCode: z.string()
    .max(10, 'Zip Code is too long')
    .optional()
});

export type CreateUpdateContactForm = z.infer<typeof createUpdateContactFormSchema>;

// Default values with type inference for controlled inputs
export const createUpdateContactFormDefaults = {
  client: null,
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  workPhone: '',
  mobilePhone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: ''
} as CreateUpdateContactForm;

type UseCreateUpdateContactFormOptions = {
  defaultValues?: Partial<CreateUpdateContactForm>;
  onSubmit: (values: CreateUpdateContactForm) => Promise<void> | void;
};

export function useCreateUpdateContactForm({ defaultValues, onSubmit }: UseCreateUpdateContactFormOptions) {
  return useAppForm({
    defaultValues: { ...createUpdateContactFormDefaults, ...defaultValues },
    validators: {
      onChange: createUpdateContactFormSchema
    },
    onSubmit: async ({ formApi, value }) => {
      const sanitizedValues = {
        client: value.client,
        firstName: value.firstName,
        lastName: value.lastName,
        title: sanitizeStringForApi(value.title),
        email: sanitizeStringForApi(value.email),
        workPhone: sanitizeStringForApi(value.workPhone),
        mobilePhone: sanitizeStringForApi(value.mobilePhone),
        addressLine1: sanitizeStringForApi(value.addressLine1),
        addressLine2: sanitizeStringForApi(value.addressLine2),
        city: sanitizeStringForApi(value.city),
        state: sanitizeStringForApi(value.state),
        zipCode: sanitizeStringForApi(value.zipCode)
      };

      await onSubmit(sanitizedValues);
      formApi.reset();
    }
  });
}
